<script lang="ts">
import { gsap } from 'gsap'
import { defineComponent, onUnmounted, ref, watch } from 'vue'

import LayerGrid from '@/components/atoms/LayerGrid.vue'

export default defineComponent({
  name: 'LayerLinearGrid',
  components: {
    LayerGrid
  },
  props: {
    defaultVisible: { type: Boolean, default: false },
    duration: { type: Number, default: 0.5 }
  },
  setup (props, { attrs, slots, emit, expose }) {
    const els = ref()
    const ready = ref(false)
    const tween = ref<gsap.core.Timeline>()
    let lastState
    let lastProgress = 0

    const createTimeline = () => {
      tween.value?.kill()
      tween.value = gsap.timeline({
        paused: true,
        onUpdate () { lastProgress = this.progress() },
        onComplete () { lastProgress = 1 }
      })

      const stepTime = 1 / els.value.length
      tween.value.addLabel('start')
      els.value.forEach((item, i) => {
        gsap.set(item, { autoAlpha: props.defaultVisible ? 1 : 0 })
        tween.value.to(item,
          {
            autoAlpha: props.defaultVisible ? 0 : 1,
            ease: 'none',
            duration: 0.1
          }, stepTime * i)
      })
      tween.value.addLabel('end')
    }

    const onRefresh = (_els) => {
      els.value = [..._els].sort(() => Math.random() - 0.5)
      createTimeline()
      ready.value = true
    }

    const animate = (show: boolean, opts?: gsap.TweenVars) => {
      lastState = { show, opts }
      if (!tween.value) return
      const target = show
        ? props.defaultVisible ? 'start' : 'end'
        : props.defaultVisible ? 'end' : 'start'
      return tween.value.tweenTo(target, { ease: 'none', ...opts })
    }
    const show = (opts?: gsap.TweenVars) => animate(true, opts)
    const hide = (opts?: gsap.TweenVars) => animate(false, opts)

    const setProgress = (value: number) => {
      lastState = undefined
      lastProgress = value
      tween.value?.progress(lastProgress)
    }

    onUnmounted(() => tween.value?.kill())

    watch(tween, _tween => {
      if (!tween.value) return
      tween.value.progress(lastProgress)
      if (lastState) animate(lastState.show, lastState.opts)
    })

    expose({ show, hide, setProgress, ready })

    return {
      onRefresh
    }
  }
})
</script>

<template>
  <LayerGrid @refresh="onRefresh" />
</template>
